import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { type CardDisputeEvidence, cardsGetCardDisputeDocument } from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';
import {
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { Section } from '~/components';
import { useCanManageCardDisputes } from '~/lib/api/cardDisputes';
import { CARD_DISPUTE_EVIDENCE_TYPE_LABELS, DISPUTE_SYNC_NOTE } from '~/lib/disputes/labels';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { downloadFile, formatDateTime } from '~/util';

import { CardDisputeProps } from './CardDispute';
import { DisputeEvidenceComposerSlot } from './slots';

interface EvidenceTableProps {
  entries: CardDisputeEvidence[];
  emptyTitle: string;
  emptyDescription: string;
}

const EvidenceTable = ({ entries, emptyTitle, emptyDescription }: EvidenceTableProps) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [downloadingId, setDownloadingId] = useState<string | undefined>();

  const handleDownload = (evidence: CardDisputeEvidence) => {
    if (!evidence.document_id) return;

    setDownloadingId(evidence.id);
    // Dispute evidence lives in PCI-scoped storage; the generic /documents/{id} endpoint rejects
    // card_dispute_evidence documents, so downloads must use the dispute document endpoint.
    cardsGetCardDisputeDocument({
      path: { document_id: evidence.document_id },
      query: { content_disposition: 'attachment' },
      throwOnError: true,
    })
      .then(({ data }) => {
        downloadFile(data.url, `evidence-${evidence.id}`);
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message || 'Failed to download evidence document',
        });
      })
      .finally(() => setDownloadingId((current) => (current === evidence.id ? undefined : current)));
  };

  const columns: DataTableColumnDef<CardDisputeEvidence>[] = [
    {
      accessor: 'evidence_type',
      header: 'Type',
      width: 'minmax(min-content, 200px)',
      cell: ({ row }) => (
        <span>{row.evidence_type ? CARD_DISPUTE_EVIDENCE_TYPE_LABELS[row.evidence_type] : 'Narrative'}</span>
      ),
    },
    {
      accessor: 'narrative',
      header: 'Narrative',
      width: 'minmax(240px, 2fr)',
      className: 'min-w-0',
      cell: ({ row }) => (
        <span className="line-clamp-2 min-w-0 whitespace-pre-wrap text-muted-foreground">{row.narrative ?? '—'}</span>
      ),
    },
    {
      accessor: 'submitted_at',
      header: 'Delivered to Network',
      width: 'min-content',
      cell: ({ row }) =>
        row.submitted_at ? (
          <Timestamp
            date={row.submitted_at}
            formatHover={(hoverDate) => formatDateTime(hoverDate, { timeZoneFormat: 'utc' })}
          >
            {formatDateTime(row.submitted_at, { timeZoneFormat: 'local' })}
          </Timestamp>
        ) : (
          <span className="text-muted-foreground">Not sent</span>
        ),
    },
    {
      accessor: 'created_at',
      header: 'Added',
      width: 'min-content',
      cell: ({ row }) => (
        <Timestamp date={row.created_at}>{formatDateTime(row.created_at, { timeZoneFormat: 'local' })}</Timestamp>
      ),
    },
    {
      accessor: 'id',
      header: '',
      width: 'min-content',
      cell: ({ row }) =>
        row.document_id ? (
          <Button
            variant="secondary"
            size="small"
            onClick={() => handleDownload(row)}
            icon={
              <Icon.AnimationDownload
                running={downloadingId === row.id}
                onDone={() => setDownloadingId((current) => (current === row.id ? undefined : current))}
              />
            }
          >
            Download
          </Button>
        ) : null,
    },
  ];

  if (entries.length === 0) {
    return (
      <Empty className="min-h-[160px]">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon.Document />
          </EmptyMedia>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyDescription>{emptyDescription}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <DataTable<CardDisputeEvidence> columns={columns} data={entries} rowKey={(row) => row.id} rowClassName="min-h-11" />
  );
};

export const PageCardDisputeEvidence: React.FC = () => {
  const { cardDispute } = useOutletContext<CardDisputeProps>();
  const canManage = useCanManageCardDisputes();

  if (!cardDispute) {
    return null;
  }

  const outboundEvidence = cardDispute.evidence.filter((entry) => entry.direction === 'outbound');
  const inboundEvidence = cardDispute.evidence.filter((entry) => entry.direction === 'inbound');

  return (
    <Inner p={0}>
      <Section headline="Submitted by you">
        <EvidenceTable
          entries={outboundEvidence}
          emptyTitle="No evidence submitted"
          emptyDescription="Evidence you attach to this dispute will appear here."
        />
        {canManage && (
          <DisputeEvidenceComposerSlot
            value={[]}
            onChange={() => {
              /* Part 03 attaches uploaded document ids through the evidence/respond APIs. */
            }}
            disabled={false}
          />
        )}
      </Section>

      <Section
        headline="Received from merchant"
        description={`Merchant responses appear here automatically. ${DISPUTE_SYNC_NOTE}`}
      >
        <EvidenceTable
          entries={inboundEvidence}
          emptyTitle="Nothing received from the merchant yet"
          emptyDescription={`Merchant responses appear here automatically. ${DISPUTE_SYNC_NOTE}`}
        />
      </Section>
    </Inner>
  );
};
